// ============================================================
// useUnread (v8.363.3) - what I have not read, per conversation.
//
// `lastReadAt` is a VIEWER preference (`chatRead:<sessionId>`), not a
// seat field: which lines you have seen is your browser's business, and
// viewer prefs already have the one store, read through
// useSyncExternalStore like every other pref.
// ============================================================
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../utils/viewerPrefs';
import { unreadCounts, type ChatMessage, type ConversationKey, type Unread } from '../engine/chat';

/** The stored read marks for a key - an unset or malformed pref reads as none. */
function readMarks(prefs: Record<string, unknown>, key: string | null): Record<string, number> {
  const v = key ? prefs[key] : null;
  return v && typeof v === 'object' ? (v as Record<string, number>) : {};
}

export default function useUnread(
  sessionId: string | null,
  messages: ChatMessage[],
  me: string,
): {
  unread: Unread;
  lastReadAt: Record<string, number>;
  markRead: (conv: ConversationKey, ts: number) => void;
} {
  const prefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs, getViewerPrefs) as Record<string, unknown>;
  const key = sessionId ? `chatRead:${sessionId}` : null;
  const lastReadAt = useMemo(() => readMarks(prefs, key), [prefs, key]);
  const unread = useMemo(() => unreadCounts(messages, lastReadAt, me), [messages, lastReadAt, me]);
  /** Mark a conversation read up to `ts` - never backwards, and only inside a session. */
  const markRead = useCallback(
    (conv: ConversationKey, ts: number) => {
      if (!key) return;
      const have = readMarks(getViewerPrefs() as Record<string, unknown>, key);
      if ((have[conv] ?? 0) >= ts) return;
      setViewerPref(key, { ...have, [conv]: ts });
    },
    [key],
  );
  return { unread, lastReadAt, markRead };
}
